import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { cn } from '../../../lib/utils';
import { TagChip } from './TagChip';
import { TagPicker } from './TagPicker';
import { tagsApi } from './tagsApi';
import type { TagSummary } from '@/generated/api/models/tagSummary';

export interface TagInputProps {
  /** {module}.{entity} string. Required for the auto-save path; the API also uses it for autocomplete personalization. */
  entityType: string;
  entityId?: string;
  value: TagSummary[];
  /** Called after the server confirms a change so React Query / parent state can refresh. */
  onChange: (next: TagSummary[]) => void;
  /**
   * If true, the component issues PUT /api/v1/tags/entity/... directly
   * (auto-save matching detail-page inline-edit pattern). If false the
   * parent owns persistence (e.g. inside a Quick Create form).
   *
   * Default: true when `entityId` is given, false otherwise.
   */
  autoSave?: boolean;
  className?: string;
  readOnly?: boolean;
}

/**
 * Inline editor for an entity's tag set.
 *
 * Renders the current tags as chips with a remove (X) affordance plus an
 * "Add tag" trigger that opens TagPicker. On a detail page, auto-saves via
 * the entity-tag endpoints; inside a form, defers persistence to the parent.
 */
export function TagInput({
  entityType,
  entityId,
  value,
  onChange,
  autoSave,
  className,
  readOnly = false,
}: TagInputProps) {
  const { t } = useTranslation('common');
  const [pending, setPending] = useState(false);

  const effectiveAutoSave = autoSave ?? !!entityId;

  const persist = useCallback(
    async (next: TagSummary[]) => {
      if (!effectiveAutoSave || !entityId) {
        onChange(next);
        return;
      }
      setPending(true);
      try {
        const resp = await tagsApi.replaceForEntity(
          entityType,
          entityId,
          next.map((tag) => tag.id),
        );
        onChange(resp.tags);
      } catch (err) {
        // eslint-disable-next-line no-restricted-syntax -- bare non-mutation call, no global handler fires
        toast.error(t('app.tagSaveFailed', { defaultValue: 'Failed to update tags' }));
        throw err;
      } finally {
        setPending(false);
      }
    },
    [effectiveAutoSave, entityId, entityType, onChange, t],
  );

  const handleAdd = useCallback(
    async (tag: TagSummary) => {
      if (value.some((t) => t.id === tag.id)) return;
      const next = [...value, tag];
      await persist(next);
    },
    [persist, value],
  );

  const handleCreate = useCallback(
    async (name: string) => {
      try {
        const created = await tagsApi.create({ name });
        await handleAdd({ id: created.id, name: created.name, color: created.color });
      } catch (err) {
        // eslint-disable-next-line no-restricted-syntax -- bare non-mutation call, no global handler fires
        toast.error(t('app.tagCreateFailed', { defaultValue: 'Failed to create tag' }));
        throw err;
      }
    },
    [handleAdd, t],
  );

  const handleRemove = useCallback(
    async (id: string) => {
      const next = value.filter((tag) => tag.id !== id);
      await persist(next);
    },
    [persist, value],
  );

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {value.map((tag) => (
        <TagChip
          key={tag.id}
          tag={tag}
          entityType={entityType}
          onRemove={readOnly ? undefined : () => handleRemove(tag.id)}
        />
      ))}
      {!readOnly && (
        <TagPicker
          entityType={entityType}
          excludeIds={value.map((tag) => tag.id)}
          onPick={(suggestion) =>
            handleAdd({ id: suggestion.id, name: suggestion.name, color: suggestion.color })
          }
          onCreate={handleCreate}
        />
      )}
      {pending && (
        <span className="text-[11px] text-muted-foreground" aria-live="polite">
          {t('app.saving', { defaultValue: 'Saving…' })}
        </span>
      )}
    </div>
  );
}
